import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../../constants/KILO_THEME'
import { PRIMARY_METRIC } from '../../lib/hardware/hardwareCatalog'
import type { BleTelemetryController } from '../../lib/hardware/bleManager'
import { formatSplit } from '../../lib/hardware/pm5Parser'
import { useLiveTelemetry, type PreconnectedSession } from '../../lib/hardware/useLiveTelemetry'
import type { SessionMarkers } from '../../lib/hardware/sessionAggregator'
import type { DiscoveredPeripheral, MachineKind, TelemetryMetric } from '../../lib/hardware/types'
import { useHardwareMetricLabels } from '../../hooks/useHardwareMetricLabels'

type LiveTelemetryScreenProps = {
  /** Target HR for the active movement profile; drives the intervention trigger. */
  targetHr: number
  machineName?: string
  /** Live controller handed off from the pairing step (skips a 2nd BLE prompt). */
  liveController?: BleTelemetryController | null
  livePeripheral?: DiscoveredPeripheral | null
  onSessionComplete?: (result: { markers: SessionMarkers; strainScore: number | null }) => void
}

function formatMetric(metric: TelemetryMetric, value: number): string {
  if (metric === 'split_500m_sec') return formatSplit(value)
  if (metric === 'mean_velocity_ms' || metric === 'peak_velocity_ms') return value.toFixed(2)
  if (metric === 'speed_kmh') return value.toFixed(1)
  if (metric === 'incline_pct') return value.toFixed(1)
  return Math.round(value).toString()
}

const SECONDARY_METRICS: Record<MachineKind, TelemetryMetric[]> = {
  treadmill: ['incline_pct', 'heart_rate_bpm', 'distance_m', 'calories_kcal'],
  indoor_bike: ['cadence_rpm', 'resistance_level', 'heart_rate_bpm', 'calories_kcal'],
  rower: ['stroke_rate', 'split_500m_sec', 'distance_m', 'heart_rate_bpm'],
  ski_erg: ['stroke_rate', 'distance_m', 'heart_rate_bpm', 'calories_kcal'],
  elliptical: ['cadence_rpm', 'resistance_level', 'heart_rate_bpm', 'calories_kcal'],
  stair_climber: ['stride_rate', 'heart_rate_bpm', 'calories_kcal', 'elapsed_s'],
  vbt_bar: ['peak_velocity_ms', 'load_kg', 'rep_count', 'time_under_tension_s'],
  unknown: ['heart_rate_bpm', 'elapsed_s'],
}

const LiveTelemetryScreen = ({
  targetHr,
  machineName,
  liveController,
  livePeripheral,
  onSessionComplete,
}: LiveTelemetryScreenProps) => {
  const { t } = useTranslation()
  const { metricLabels } = useHardwareMetricLabels()
  const preconnected = useMemo<PreconnectedSession | null>(
    () => (liveController && livePeripheral ? { controller: liveController, peripheral: livePeripheral } : null),
    [liveController, livePeripheral],
  )
  const {
    available,
    state,
    recoveryState,
    peripheral,
    metrics,
    intervention,
    error,
    discoverAndStream,
    dismissIntervention,
    resumeFromFallback,
    endSession,
  } = useLiveTelemetry(targetHr, preconnected)
  const [ending, setEnding] = useState(false)
  const reconnecting = recoveryState === 'RECONNECTING_SILENT'
  const droppedOut = recoveryState === 'DISCONNECTED_FALLBACK'

  const kind = peripheral?.machineKind ?? 'unknown'
  const primary = PRIMARY_METRIC[kind]
  const primaryValue = metrics[primary]
  const secondary = SECONDARY_METRICS[kind]

  async function handleEnd() {
    setEnding(true)
    const result = await endSession()
    setEnding(false)
    onSessionComplete?.(result)
  }

  return (
    <div className="relative min-h-[520px] overflow-hidden rounded-3xl bg-black p-6">
      {/* Intervention overlay */}
      {intervention ? (
        <div className="absolute inset-x-0 top-0 z-20 animate-pulse-once">
          <div className="m-3 rounded-2xl border border-orange-500/60 bg-gradient-to-r from-orange-950 to-red-950 p-4 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-orange-400">
                  ⚠ {t('workout.live.strainHigh')}
                </p>
                <p className="mt-1 text-sm font-medium text-white">{intervention.suggestion}</p>
                <p className="mt-1 text-xs text-orange-300/80">
                  {t('workout.live.hrOverTarget', {
                    hr: intervention.observedHr,
                    delta: intervention.deltaBpm,
                    target: intervention.targetHr,
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={dismissIntervention}
                className="rounded-lg bg-black/40 px-2 py-1 text-xs text-orange-200 hover:bg-black/60"
              >
                {t('workout.live.dismiss')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Connection / scan state */}
      {state === 'idle' || state === 'error' ? (
        <div className="flex h-[460px] flex-col items-center justify-center gap-4 text-center">
          <div className="text-5xl">🏋️</div>
          <h3 className="text-xl font-semibold text-white">{t('workout.live.hudTitle')}</h3>
          <p className="max-w-xs text-sm text-neutral-400">
            {available ? t('workout.live.connectPrompt') : t('workout.live.bleUnavailable')}
          </p>
          {error ? <p className="text-xs text-red-400">{error}</p> : null}
          <button
            type="button"
            onClick={() => void discoverAndStream()}
            disabled={!available}
            className="rounded-xl bg-kilo-accent px-6 py-3 font-semibold text-black transition-colors hover:bg-kilo-accent-dark disabled:opacity-50"
          >
            {t('workout.live.connectMachine')}
          </button>
        </div>
      ) : (
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between">
            <div>
              <p className={KILO.meta}>{peripheral?.name ?? machineName ?? t('workout.live.fitnessMachine')}</p>
              {reconnecting ? (
                <p className="mt-1 flex items-center gap-2 text-xs text-amber-400">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-amber-400" />
                  {t('workout.live.reconnecting')}
                </p>
              ) : droppedOut ? (
                <p className="mt-1 flex items-center gap-2 text-xs text-red-400">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  {t('workout.live.disconnected')}
                </p>
              ) : (
                <p className="mt-1 flex items-center gap-2 text-xs text-kilo-accent">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-kilo-accent" />
                  {state === 'streaming' ? t('workout.live.streamingLive') : state}
                </p>
              )}
            </div>
            <span className="rounded-full bg-neutral-900 px-3 py-1 text-[10px] uppercase tracking-wider text-neutral-400">
              {peripheral?.connectionType.replace('LOCAL_', '').replace('_', ' ')}
            </span>
          </div>

          {/* Gold metric */}
          <div className="flex flex-1 flex-col items-center justify-center py-8">
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-neutral-500">
              {metricLabels[primary]?.label}
            </p>
            <p className="mt-2 font-black tabular-nums text-white" style={{ fontSize: '5.5rem', lineHeight: 1 }}>
              {primaryValue != null ? formatMetric(primary, primaryValue) : '--'}
            </p>
            <p className="text-lg text-neutral-400">{metricLabels[primary]?.unit}</p>
          </div>

          {/* Secondary metrics */}
          <div className="grid grid-cols-4 gap-2">
            {secondary.map((metric) => (
              <div key={metric} className="rounded-xl border border-neutral-800 bg-neutral-900 p-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                  {metricLabels[metric]?.label}
                </p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-white">
                  {metrics[metric] != null ? formatMetric(metric, metrics[metric] as number) : '--'}
                </p>
              </div>
            ))}
          </div>

          {droppedOut ? (
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-red-900/60 bg-red-950/40 px-4 py-3">
              <p className="text-xs text-red-300">{t('workout.live.reconnectPrompt')}</p>
              <button
                type="button"
                onClick={resumeFromFallback}
                className="shrink-0 rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-400"
              >
                {t('workout.live.reconnect')}
              </button>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => void handleEnd()}
            disabled={ending}
            className="mt-4 w-full rounded-xl border border-neutral-700 bg-neutral-900 py-3 font-semibold text-white transition-colors hover:bg-neutral-800 disabled:opacity-50"
          >
            {ending ? t('workout.live.finalizing') : t('workout.live.endSession')}
          </button>
        </div>
      )}
    </div>
  )
}

export default LiveTelemetryScreen
